import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../context/ThemeContext';
import * as api from '../../lib/api';
import { buttonClass, fieldClass, labelClass, mutedClass } from '../uiStyles';

// Mirrors the server's limits so the user hears about a too-short message
// before the round trip.
const SUBJECT_MIN = 3;
const BODY_MIN = 5;
const BODY_MAX = 5000;

const NewThreadForm = ({ onCreated, onCancel }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const valid = subject.trim().length >= SUBJECT_MIN && body.trim().length >= BODY_MIN;

  const submit = async (event) => {
    event.preventDefault();
    if (!valid || sending) return;

    setSending(true);
    setError(null);
    try {
      const data = await api.createThread(subject.trim(), body.trim());
      setSubject('');
      setBody('');
      if (onCreated) onCreated(data.thread);
    } catch (caught) {
      setError(caught.code || 'server-error');
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label className={labelClass(isDarkMode)} htmlFor="thread-subject">
          {t('messages.subject')}
        </label>
        <input
          id="thread-subject"
          value={subject}
          onChange={(event) => setSubject(event.target.value)}
          maxLength={150}
          placeholder={t('messages.subjectPlaceholder')}
          className={fieldClass(isDarkMode)}
        />
      </div>

      <div>
        <label className={labelClass(isDarkMode)} htmlFor="thread-body">
          {t('messages.body')}
        </label>
        <textarea
          id="thread-body"
          value={body}
          onChange={(event) => setBody(event.target.value)}
          rows={6}
          maxLength={BODY_MAX}
          placeholder={t('messages.bodyPlaceholder')}
          className={`${fieldClass(isDarkMode)} resize-y text-sm`}
        />
        <p className={`text-xs mt-1 ${mutedClass(isDarkMode)}`}>{body.length} / {BODY_MAX}</p>
      </div>

      {error && (
        <p className="text-sm text-red-500">
          {t([`messages.errors.${error}`, 'messages.errors.server-error'])}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={!valid || sending} className={buttonClass(isDarkMode, 'primary')}>
          {sending ? t('messages.sending') : t('messages.sendToAdmin')}
        </button>
        {onCancel && (
          <button type="button" onClick={onCancel} className={buttonClass(isDarkMode)}>
            {t('messages.cancel')}
          </button>
        )}
      </div>
    </form>
  );
};

export default NewThreadForm;
